/**
 * Active Workspace Component
 * Phase 7: Workspace Surface Layer - Main Content Area
 * 
 * Renders the active workspace content based on selected page.
 * Dynamically loads appropriate surface (HomePage, PageEditor, DatabaseView, etc.)
 */

import React, { useMemo, Suspense } from 'react';
import { Box, Center, Loader, Stack, Text, useMantineColorScheme } from '@mantine/core';
import { Card } from '../../primitives/Card';
import { IconHome } from '@tabler/icons-react';
import { useWorkspace } from '../../../core/workspace/WorkspaceContext';
import { loadDatabase, saveDatabase } from '../../../core/workspace/databaseStorage';
import { seedPageContent } from '../../../core/workspace/pageContentStorage';
import { eventBus } from '../../../core/event-bus';
import type { DatabaseItem } from '../../../types/models/Database';
import HomePage from './HomePage';
import PageEditor from './PageEditor';
import DatabaseView from './DatabaseView';
import { TodayView } from '../task/TodayView';

/**
 * Active Workspace Props
 */
interface ActiveWorkspaceProps {
  /** Custom className */
  className?: string;
  /** Custom style */
  style?: React.CSSProperties;
}

/**
 * Loading Fallback Component
 */
const WorkspaceLoading: React.FC = () => (
  <Center h="100%" w="100%">
    <Stack align="center" gap="md">
      <Loader size="lg" />
      <Text c="dimmed" size="sm">Loading workspace...</Text>
    </Stack>
  </Center>
);

/**
 * Home View - Notion-style gallery with page cards
 */
const HomeView: React.FC = () => {
  const { pages, selectPage, createPage } = useWorkspace();
  const { colorScheme } = useMantineColorScheme();
  
  // Use CSS variables from the FreshCards theme context so the colors
  // follow the theme without racing Mantine's color scheme hook.
  const bgPrimary = 'var(--fc-bg-primary, #ffffff)';
  const bgHover = 'var(--fc-bg-hover, #f7f6f3)';
  const textPrimary = 'var(--fc-text-primary, #37352f)';
  const textMuted = 'var(--fc-text-hint, #8e8e8e)';
  const borderColor = 'var(--fc-border-primary, #e9e9e7)';

  const handleCreatePage = async (template?: string) => {
    const titles: Record<string, string> = {
      'blank': 'Untitled',
      'meeting': 'Meeting Notes',
      'task': 'Task List',
      'doc': 'Documentation',
      'database': 'Untitled Database',
    };
    const types: Record<string, 'page' | 'database'> = {
      'blank': 'page',
      'meeting': 'page',
      'task': 'page',
      'doc': 'page',
      'database': 'database',
    };
    try {
      const newPage = await createPage(
        titles[template || 'blank'] || 'Untitled',
        undefined,
        types[template || 'blank'] || 'page'
      );
      // For page templates, seed the editor with template content
      if (types[template || 'blank'] === 'page') {
        seedPageContent(newPage.id, template || 'blank');
      }
      await selectPage(newPage.id);
    } catch (e) {
      console.error('Failed to create page:', e);
    }
  };

  /**
   * Seed a "Quick Notes" database with sample kanban items.
   * Re-uses an existing "Quick Notes" page if one already exists, otherwise creates a new one.
   * Then writes 4 sample items into the underlying database and navigates to it.
   */
  const handleSeedDemo = async () => {
    try {
      const existing = pages.find(p => p.title === 'Quick Notes');
      let pageId: string;
      if (existing) {
        pageId = existing.id;
        await selectPage(pageId);
      } else {
        const newPage = await createPage('Quick Notes', undefined, 'database');
        pageId = newPage.id;
      }
      // The page id doubles as the database id (see WorkspaceContext.createPage).
      const db = loadDatabase(pageId);
      if (!db) {
        console.error('[SeedDemo] No database found for pageId', pageId);
        return;
      }
      // Don't double-seed — bail if items already present.
      if (db.items.length > 0) {
        await selectPage(pageId);
        return;
      }
      const now = new Date();
      // 30 diverse items — a realistic product-team Quick Notes database.
      // Uses inline CSS gradients for covers (no network, no flicker,
      // theme-aware, never blocks render).
      const GRADIENT_COVERS = [
        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
        'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
        'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
        'linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)',
        'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',
        'linear-gradient(135deg, #cd9cf2 0%, #f6f3ff 100%)',
        'linear-gradient(135deg, #fddb92 0%, #d1fdff 100%)',
        'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
        'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
      ];
      type SeedStatus = 'backlog' | 'in_progress' | 'review' | 'done' | 'archived';
      type SeedPriority = 'urgent' | 'high' | 'medium' | 'low';
      type SeedAssignee = 'me' | 'alex' | 'sam' | 'priya' | 'jordan' | 'kai';

      const STATUS_DEFS: Record<SeedStatus, { name: string; color: string; state: string }> = {
        backlog:     { name: 'Backlog',     color: '#94a3b8', state: 'active' },
        in_progress: { name: 'In Progress', color: '#3b82f6', state: 'active' },
        review:      { name: 'Review',      color: '#a855f7', state: 'active' },
        done:        { name: 'Done',        color: '#22c55e', state: 'completed' },
        archived:    { name: 'Archived',    color: '#475569', state: 'archived' },
      };
      const PRIORITY_DEFS: Record<SeedPriority, { name: string; color: string; rank: number }> = {
        urgent: { name: '🔥 Urgent', color: '#ef4444', rank: 1 },
        high:   { name: '⚡ High',   color: '#f97316', rank: 2 },
        medium: { name: '◐ Medium', color: '#eab308', rank: 3 },
        low:    { name: '✓ Low',    color: '#22c55e', rank: 4 },
      };
      const ASSIGNEE_DEFS: Record<SeedAssignee, { name: string; color: string }> = {
        me:    { name: 'Me',      color: '#0ea5e9' },
        alex:  { name: 'Alex',    color: '#ec4899' },
        sam:   { name: 'Sam',     color: '#8b5cf6' },
        priya: { name: 'Priya',   color: '#10b981' },
        jordan: { name: 'Jordan', color: '#f59e0b' },
        kai:   { name: 'Kai',     color: '#06b6d4' },
      };
      const TAGS = ['design', 'eng', 'research', 'bug', 'feature', 'docs', 'meeting', 'idea'];

      // (title, status, priority, assignee, tags, daysFromNow, coverSeed)
      const SEEDS: Array<[string, SeedStatus, SeedPriority, SeedAssignee, string[], number, string]> = [
        ['Ship kanban drag-and-drop',                'done',        'high',   'me',    ['eng', 'feature'],  -7,  'kanban1'],
        ['Add multi-select tag filter',              'in_progress', 'medium', 'alex',  ['eng', 'feature'],  -2,  'tags1'],
        ['Wire up status property to board columns', 'in_progress', 'high',   'me',    ['eng', 'feature'],  -1,  'wires1'],
        ['Write seed-data dev shortcut',             'done',        'low',    'sam',   ['eng', 'docs'],     -10, 'docs1'],
        ['Design system: spacing tokens',            'done',        'medium', 'priya', ['design'],         -5,  'design1'],
        ['Dark mode polish pass',                    'in_progress', 'medium', 'priya', ['design'],         -3,  'dark1'],
        ['Calendar mobile responsive',              'backlog',     'high',   'me',    ['design', 'eng'],    2,  'mobile1'],
        ['Timeline (Gantt) view',                    'backlog',     'medium', 'alex',  ['eng', 'feature'],   3,  'timeline1'],
        ['Seed data with cover images',              'in_progress', 'medium', 'sam',   ['eng', 'feature'],  -1,  'images1'],
        ['Black cards on non-dark mode',             'done',        'low',    'priya', ['design', 'bug'],   -4,  'cards1'],
        ['Sidebar mobile close button',              'done',        'high',   'me',    ['eng', 'bug'],      -6,  'sidebar1'],
        ['View config registry',                     'done',        'high',   'me',    ['eng'],             -8,  'registry1'],
        ['Recent view (last edited)',                'in_progress', 'medium', 'alex',  ['eng', 'feature'],  -2,  'recent1'],
        ['Atlas module_ref on every surface',        'done',        'medium', 'me',    ['eng'],             -9,  'atlas1'],
        ['Module Register as 9th artefact',          'done',        'high',   'sam',   ['eng'],            -10,  'register1'],
        ['Pact declarations refreshed',              'done',        'low',    'kai',   ['eng', 'docs'],     -7,  'pact1'],
        ['Tauri 2 config bumped to 0.8.0',           'backlog',     'low',    'kai',   ['eng'],              5,  'tauri1'],
        ['User research: empty state',               'review',      'high',   'priya', ['research', 'design'], -1, 'research1'],
        ['A/B test: 3-tab peek vs 2-tab peek',       'backlog',     'medium', 'alex',  ['research'],        7,  'ab1'],
        ['Bug: gallery empty after switching from kanban', 'done',  'urgent', 'me',    ['eng', 'bug'],      -3,  'bug1'],
        ['Fix: white page on seed flow',             'done',        'urgent', 'me',    ['eng', 'bug'],      -1,  'fix1'],
        ['Weekly retro notes',                       'done',        'low',    'sam',   ['meeting'],         -8,  'retro1'],
        ['Sprint planning — Q3',                     'review',      'high',   'sam',   ['meeting'],         -2,  'planning1'],
        ['Idea: AI-suggested tags',                  'backlog',     'low',    'kai',   ['idea'],            14, 'idea1'],
        ['Idea: collaborative cursors',              'archived',    'low',    'kai',   ['idea'],           -20, 'collab1'],
        ['Performance: virtualise gallery',          'backlog',     'medium', 'me',    ['eng', 'feature'],   10, 'perf1'],
        ['Accessibility: keyboard nav for kanban',    'review',      'high',   'priya', ['design', 'eng'],   -2, 'a11y1'],
        ['Documentation: Quick start guide',          'in_progress', 'medium', 'sam',   ['docs'],            -1, 'docs2'],
        ['Roadmap: V5.1 ship party',                 'backlog',     'low',    'sam',   ['meeting'],         21, 'party1'],
        ['Idea: voice notes in card',                'archived',    'low',    'kai',   ['idea'],           -30, 'voice1'],
      ];

      const dayMs = 24 * 60 * 60 * 1000;
      const baseTime = Date.now();

      const mkItem = (i: number, seed: typeof SEEDS[number]): DatabaseItem => {
        const [title, status, priority, assignee, tags, daysFromNow] = seed;
        const stDef = STATUS_DEFS[status];
        const prDef = PRIORITY_DEFS[priority];
        const asDef = ASSIGNEE_DEFS[assignee];
        const dueDate = new Date(baseTime + daysFromNow * dayMs);
        const createdAt = new Date(baseTime - Math.abs(daysFromNow) * dayMs - 3600_000);
        return {
          id: `${pageId}_i${i}`,
          databaseId: pageId,
          title,
          properties: [
            { propertyId: 'title', value: title },
            { propertyId: 'date', value: dueDate.toISOString().split('T')[0] },
            {
              propertyId: 'status',
              value: { id: `status_${status}`, name: stDef.name, color: stDef.color, state: stDef.state },
            },
            {
              propertyId: 'priority',
              value: { id: `prio_${priority}`, name: prDef.name, color: prDef.color, rank: prDef.rank },
            },
            {
              propertyId: 'assignee',
              value: { id: `asg_${assignee}`, name: asDef.name, color: asDef.color },
            },
            { propertyId: 'tag', value: tags },
            { propertyId: 'finished', value: status === 'done' || status === 'archived' },
            { propertyId: 'cover', value: GRADIENT_COVERS[i % GRADIENT_COVERS.length] },
          ],
          createdAt,
          updatedAt: createdAt,
        };
      };

      db.items = SEEDS.map((seed, i) => mkItem(i, seed));
      saveDatabase(db);
      // Tell the mounted DatabaseView to re-load from storage so it picks up
      // the freshly-seeded items (it only reads from storage on mount).
      eventBus.emit('database:refresh', { databaseId: pageId });
      await selectPage(pageId);
    } catch (e) {
      console.error('[SeedDemo] Failed:', e);
    }
  };

  const templateCards = [
    { id: 'blank', emoji: '📄', title: 'Blank Page', desc: 'Start with a clean slate' },
    { id: 'meeting', emoji: '📅', title: 'Meeting Notes', desc: 'Capture meeting outcomes' },
    { id: 'task', emoji: '✅', title: 'Task List', desc: 'Track your tasks and to-dos' },
    { id: 'doc', emoji: '📝', title: 'Documentation', desc: 'Write technical docs' },
    { id: 'database', emoji: '🗂', title: 'New Database', desc: 'Kanban / table / list view' },
    { id: 'seed', emoji: '🚀', title: 'Seed demo data', desc: 'Populate a "Quick Notes" kanban with sample cards', accent: true },
  ];

  return (
    <Box 
      h="100%" 
      w="100%" 
      style={{ overflow: 'auto', background: bgPrimary }}
    >
      <Box p="xl" style={{ maxWidth: 1000, margin: '0 auto' }}>
        {/* Header */}
        <Box mb="xl">
          <Text size="xl" fw={700} mb={4} style={{ color: textPrimary }}>Home</Text>
          <Text size="sm" c="dimmed">Welcome to your workspace</Text>
        </Box>

        {/* Quick Actions - Templates */}
        <Box mb="xl">
          <Text fw={600} mb="md" size="sm" c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Quick Create
          </Text>
          <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
            {templateCards.map(template => {
              const isSeed = template.id === 'seed';
              return (
              <Card
                key={template.id}
                interactive
                onClick={() => isSeed ? handleSeedDemo() : handleCreatePage(template.id)}
                style={isSeed ? {
                  background: 'linear-gradient(135deg, #7c3aed22 0%, #ec489922 100%)',
                  borderColor: '#7c3aed66',
                } : undefined}
              >
                <Text size="xl" mb="xs">{template.emoji}</Text>
                <Text fw={500} size="sm" mb={4} style={{ color: textPrimary }}>{template.title}</Text>
                <Text size="xs" c="dimmed">{template.desc}</Text>
              </Card>
              );
            })}
          </Box>
        </Box>

        {/* All Pages Gallery */}
        {pages.length > 0 && (
          <Box>
            <Text fw={600} mb="md" size="sm" c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              All Pages
            </Text>
            <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
                {pages.map(page => (
                  <Card
                    key={page.id}
                    interactive
                    style={{ minHeight: 120, cursor: 'pointer' }}
                    onClick={() => selectPage(page.id)}
                  >
                    <Text fw={500} size="sm" mb="xs" style={{ color: textPrimary }}>{page.title}</Text>
                    <Text size="xs" c="dimmed">
                      {page.type === 'database' ? '📊 Database' : page.type === 'view' ? '👁 View' : '📄 Page'}
                    </Text>
                  </Card>
                ))}
            </Box>
          </Box>
        )}

        {/* Empty State */}
        {pages.length === 0 && (
          <Box 
            p="xl" 
            ta="center" 
            style={{ 
              background: bgHover, 
              borderRadius: 12,
              border: `2px dashed ${borderColor}`
            }}
          >
            <Text size="xl" mb="md" style={{ color: textMuted }}>No pages yet</Text>
            <Text c="dimmed" mb="lg">Create your first page to get started</Text>
            <Box 
              component="button"
              p="sm"
              style={{ 
                background: '#2383e2', 
                color: '#fff', 
                border: 'none', 
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 500
              }}
              onClick={() => handleCreatePage('blank')}
            >
              + Create your first page
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

/**
 * Page Editor Wrapper Component
 * Simply renders PageEditor which gets its data from context
 */
const PageEditorWrapper: React.FC = () => {
  return <PageEditor />;
};

/**
 * Database View Wrapper Component
 * Loads the database by pageId (the page id doubles as the database id)
 * and passes the real title to the DatabaseView.
 */
interface DatabaseViewWrapperProps {
  pageId: string;
  title: string;
}

const DatabaseViewWrapper: React.FC<DatabaseViewWrapperProps> = ({ pageId, title }) => {
  const { currentPage, updatePage } = useWorkspace();
  // The current page is the one with the cover / layout metadata.
  const pageCover = currentPage?.id === pageId ? currentPage.cover : undefined;
  const pageCoverLayout = currentPage?.id === pageId ? (currentPage.coverLayout || 'wide') : 'wide';
  const pageCoverPosition = currentPage?.id === pageId ? (currentPage.coverPosition ?? 50) : 50;
  return (
    <Suspense fallback={<WorkspaceLoading />}>
      <DatabaseView
        id={pageId}
        phase={7}
        title={title}
        description={`Database: ${title}`}
        isActive={true}
        pageCover={pageCover}
        pageCoverLayout={pageCoverLayout}
        pageCoverPosition={pageCoverPosition}
        onCoverChange={(cover, layout, position) => updatePage(pageId, { cover, coverLayout: layout, coverPosition: position })}
      />
    </Suspense>
  );
};

const ContentRouter: React.FC<ContentRouterProps> = ({ pageType = 'page' }) => {
  const { currentPageId, currentPage } = useWorkspace();
  const component = useMemo(() => {
    switch (pageType) {
      case 'database':
        return <DatabaseViewWrapper pageId={currentPageId ?? ''} title={currentPage?.title ?? 'Database'} />;

      case 'view':
        return <DatabaseViewWrapper pageId={currentPageId ?? ''} title={currentPage?.title ?? 'View'} />;

      case 'page':
      default:
        return <PageEditorWrapper />;
    }
  }, [pageType, currentPageId, currentPage?.title]);

  return (
    <Box h="100%" w="100%" style={{ display: 'flex', flexDirection: 'column' }}>
      {component}
    </Box>
  );
};

/**
 * Active Workspace Component
 * 
 * Main content area that displays the currently selected page's content.
 * Handles navigation between different page types and manages the active surface.
 */
export const ActiveWorkspace: React.FC<ActiveWorkspaceProps> = ({ 
  className = '', 
  style = {} 
}) => {
  const { currentPageId, currentPage, pages, initialized } = useWorkspace();

  // Determine what to render
  const renderContent = useMemo(() => {
    // Still initializing - show loading
    if (!initialized) {
      return (
        <Suspense fallback={<WorkspaceLoading />}>
          <Center h="100%" w="100%">
            <Stack align="center" gap="md">
              <Loader size="lg" />
              <Text c="dimmed" size="sm">Loading workspace...</Text>
            </Stack>
          </Center>
        </Suspense>
      );
    }

    // No page selected - show Today view (F 73 default landing).
    // TodayView renders the Inbox + Today sections; once a page is
    // selected, the ContentRouter takes over.
    if (!currentPageId || !currentPage) {
      return (
        <Suspense fallback={<WorkspaceLoading />}>
          <TodayView />
        </Suspense>
      );
    }

    // Page is selected - show appropriate content
    return (
      <ContentRouter
        pageId={currentPageId}
        pageTitle={currentPage.title}
        pageType={currentPage.type}
      />
    );
  }, [initialized, currentPageId, currentPage, pages]);

  return (
    <Box
      component="section"
      className={`active-workspace ${className}`}
      h="100%"
      w="100%"
      style={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: 'var(--fc-bg-primary)',
        ...style
      }}
    >
      {renderContent}
    </Box>
  );
};

export default ActiveWorkspace;
